import { useState, type FormEvent } from "react";
import {
  createTeacherHandover,
  listStudentTeacherHandovers,
  previewTeacherHandover,
  revokeTeacherHandover,
  type StudentTeacher,
  type TeacherHandoverContext,
  type TeacherHandoverSelection,
} from "../api.js";
import { useAsync } from "../useAsync.js";

type TutorChoice = {
  tutorProfileId: string;
  displayName: string;
};

type HandoverContextItem = {
  id: string;
  label: string;
  description?: string | null;
};

type HandoverSection = {
  key: keyof TeacherHandoverSelection;
  title: string;
  items: HandoverContextItem[];
};

const emptySelection = (): TeacherHandoverSelection => ({
  goalIds: [],
  repertoireIds: [],
  practiceTodoIds: [],
  appointmentIds: [],
});

function londonToday(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "";
  return part("year") + "-" + part("month") + "-" + part("day");
}

function formatLesson(startsAt: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/London",
  }).format(new Date(startsAt));
}

export function TeacherHandoverPanel({
  studentId,
  teacher,
  tutors,
  onChanged,
}: {
  studentId: string;
  teacher: StudentTeacher;
  tutors: TutorChoice[];
  onChanged: () => void;
}) {
  const handovers = useAsync(() => listStudentTeacherHandovers(studentId), [studentId]);
  const [open, setOpen] = useState(false);
  const [destinationTutorProfileId, setDestinationTutorProfileId] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(londonToday());
  const [summary, setSummary] = useState("");
  const [preview, setPreview] = useState<{
    source: {
      sourceAssignmentId: string;
      sourceAssignmentVersion: number;
      sourceTutorProfileId: string;
      assignments: { id: string; version: number }[];
      sourceTutorName: string;
      studentName: string;
      instrument: string;
    };
    context: TeacherHandoverContext;
  } | null>(null);
  const [selectedContext, setSelectedContext] = useState<TeacherHandoverSelection>(emptySelection);
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [createdSharePath, setCreatedSharePath] = useState<string | null>(null);
  const [createdAssignmentTransfer, setCreatedAssignmentTransfer] = useState<
    "not_scheduled" | "scheduled" | "effective" | "cancelled" | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const sourceAssignments = [...teacher.assignments].sort((left, right) => left.id.localeCompare(right.id));
  const sourceAssignment = sourceAssignments[0];
  if (!teacher.instrument || !sourceAssignment) return null;
  const sourceAssignmentId = sourceAssignment.id;
  const sourceAssignmentVersion = sourceAssignment.version;

  const sections: HandoverSection[] = preview
    ? [
        {
          key: "goalIds",
          title: "Active teaching goals",
          items: preview.context.goals.map((item) => ({
            id: item.id,
            label: item.title,
            description: item.description,
          })),
        },
        {
          key: "repertoireIds",
          title: "Current repertoire",
          items: preview.context.repertoire.map((item) => ({
            id: item.id,
            label: item.title + (item.composer ? " — " + item.composer : ""),
            description: item.instrument ?? item.status,
          })),
        },
        {
          key: "practiceTodoIds",
          title: "Unfinished practice tasks",
          items: preview.context.practiceTasks.map((item) => ({
            id: item.id,
            label: item.title,
            description: item.description,
          })),
        },
        {
          key: "appointmentIds",
          title: "Upcoming confirmed private lessons",
          items: preview.context.upcomingLessons.map((item) => ({
            id: item.id,
            label: (item.serviceName ?? "Lesson") + " — " + formatLesson(item.startsAt),
            description: item.tutorName ? "Currently with " + item.tutorName : null,
          })),
        },
      ]
    : [];

  function changedInput(): void {
    setIdempotencyKey(null);
    setCreatedSharePath(null);
    setCreatedAssignmentTransfer(null);
  }

  async function loadPreview(): Promise<void> {
    setBusy(true);
    setProblem(null);
    setCreatedSharePath(null);
    setCreatedAssignmentTransfer(null);
    try {
      const result = await previewTeacherHandover(studentId, {
        sourceAssignmentId,
        sourceAssignmentVersion,
        sourceAssignments: sourceAssignments.map(({ id, version }) => ({ id, version })),
      });
      setPreview(result);
      setSelectedContext(emptySelection());
      setIdempotencyKey(null);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "The handover preview could not be loaded.");
    } finally {
      setBusy(false);
    }
  }

  function toggleItem(key: keyof TeacherHandoverSelection, id: string): void {
    changedInput();
    setSelectedContext((current) => ({
      ...current,
      [key]: current[key].includes(id)
        ? current[key].filter((value) => value !== id)
        : [...current[key], id],
    }));
  }

  async function create(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!preview || busy || !destinationTutorProfileId) return;
    setBusy(true);
    setProblem(null);
    const retryKey = idempotencyKey ?? globalThis.crypto.randomUUID();
    setIdempotencyKey(retryKey);
    try {
      const created = await createTeacherHandover(studentId, {
        sourceAssignmentId: preview.source.sourceAssignmentId,
        sourceAssignmentVersion: preview.source.sourceAssignmentVersion,
        sourceAssignments: preview.source.assignments,
        destinationTutorProfileId,
        effectiveDate,
        summary,
        selectedContext,
        idempotencyKey: retryKey,
      });
      setCreatedSharePath(created.sharePath);
      setCreatedAssignmentTransfer(created.assignmentTransfer);
      setIdempotencyKey(null);
      handovers.reload();
      onChanged();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "The handover could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await revokeTeacherHandover(id);
      handovers.reload();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "The handover share could not be revoked.");
    } finally {
      setBusy(false);
    }
  }

  const hasSelection = Object.values(selectedContext).some((ids) => ids.length > 0);
  const destinationChoices = tutors.filter((item) => item.tutorProfileId !== teacher.tutorProfileId);

  return (
    <section className="panel" aria-label={"Handover for " + teacher.instrument}>
      <h3>Share current teaching context</h3>
      <p className="small muted">
        This shares only the items you select with the named tutor and transfers this instrument's active
        service assignments on the effective date. Provider appointments, credits and Inbox history do not
        move; manage any booking changes separately. Revoking before the date cancels the scheduled
        assignments; after it, revocation ends context sharing and the teacher must be changed separately.
      </p>
      {!open && (
        <button type="button" className="quiet small" onClick={() => setOpen(true)}>
          Prepare {teacher.instrument} handover
        </button>
      )}
      {open && (
        <div>
          <button type="button" className="quiet small" onClick={() => setOpen(false)} disabled={busy}>
            Close handover form
          </button>
          <div>
            <label>
              Destination tutor
              <select
                value={destinationTutorProfileId}
                onChange={(event) => {
                  changedInput();
                  setDestinationTutorProfileId(event.target.value);
                }}
                required
              >
                <option value="">Choose a tutor…</option>
                {destinationChoices.map((item) => (
                  <option key={item.tutorProfileId} value={item.tutorProfileId}>
                    {item.displayName}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="quiet small" onClick={() => void loadPreview()} disabled={busy}>
              {busy && !preview ? "Loading…" : "Preview current context"}
            </button>
          </div>
          {preview && (
            <form onSubmit={(event) => void create(event)}>
              <p>
                {preview.source.studentName} · {preview.source.instrument} · current teacher{" "}
                {preview.source.sourceTutorName}
              </p>
              {sections.map((section) => (
                <fieldset key={section.key}>
                  <legend>{section.title}</legend>
                  {section.items.length === 0 && <p className="small muted">Nothing available to share here.</p>}
                  {section.items.map((item) => (
                    <label key={item.id} className="row">
                      <input
                        type="checkbox"
                        checked={selectedContext[section.key].includes(item.id)}
                        onChange={() => toggleItem(section.key, item.id)}
                        disabled={busy}
                      />
                      <span>
                        {item.label}
                        {item.description && <span className="small muted"> — {item.description}</span>}
                      </span>
                    </label>
                  ))}
                </fieldset>
              ))}
              <label>
                Planned effective date
                <input
                  type="date"
                  value={effectiveDate}
                  min={londonToday()}
                  onChange={(event) => {
                    changedInput();
                    setEffectiveDate(event.target.value);
                  }}
                  required
                  disabled={busy}
                />
              </label>
              <label>
                Handover summary for the new tutor
                <textarea
                  value={summary}
                  maxLength={2000}
                  onChange={(event) => {
                    changedInput();
                    setSummary(event.target.value);
                  }}
                  required
                  disabled={busy}
                />
              </label>
              <button
                type="submit"
                disabled={busy || !destinationTutorProfileId || !effectiveDate || !summary.trim() || !hasSelection}
              >
                {busy ? "Saving…" : "Create handover share"}
              </button>
            </form>
          )}
        </div>
      )}
      {createdSharePath && (
        <p role="status">
          Handover created.{" "}
          {createdAssignmentTransfer === "scheduled"
            ? "This instrument's assignments are scheduled to transfer on " + effectiveDate + ". "
            : createdAssignmentTransfer === "effective"
              ? "This instrument's assignments have transferred. "
              : createdAssignmentTransfer === "cancelled"
                ? "The scheduled instrument transfer was cancelled. "
                : "No instrument assignment transfer was scheduled. "}
          <a href={createdSharePath}>Open or share the secure handover link</a>.
        </p>
      )}
      {handovers.loading && <p className="small muted">Loading existing handovers…</p>}
      {handovers.error && <p role="alert" className="error">{handovers.error}</p>}
      {handovers.data?.items.map((item) => (
        <div key={item.id} className="spread">
          <span>
            {item.instrument} · {item.destinationTutorName} · planned {item.effectiveDate}
            {item.revokedAt
              ? item.assignmentTransfer === "cancelled" ? " · revoked; scheduled transfer cancelled" : " · revoked"
              : item.assignmentTransfer === "scheduled" ? " · instrument transfer scheduled"
                : item.assignmentTransfer === "effective" ? " · instrument transferred" : ""}
          </span>
          <div className="row">
            <a href={"/teacher-handovers/" + encodeURIComponent(item.id)}>Open handover</a>
            {!item.revokedAt && (
              <button type="button" className="quiet small" onClick={() => void revoke(item.id)} disabled={busy}>
                {item.assignmentTransfer === "scheduled" ? "Revoke and cancel transfer" : "Revoke share"}
              </button>
            )}
          </div>
        </div>
      ))}
      {problem && <p role="alert" className="error">{problem}</p>}
    </section>
  );
}
